import { CONDITION } from '../dataSource';
import { genId, genRecord } from './utils';
import dataItemStyle from '../dataItemStyle';

const PARAM_TOTAL_LENGTH = 8;
const COMPARE_PRE_LENGTH = 8;
const CONDITION_PRE_LENGTH = 8;

const DeviceEventMap: {
  [key: string]: number;
} = {
  'objEvent@short_clicked': 4
};
const getLogicalOp = (logic_op: AnimationTrigger['logicalOperator']) =>
  logic_op === 'or' ? 0 : logic_op === 'and' ? 1 : undefined;

const getType = (type: string) =>
  type === 'data' ? 0 : type === 'objEvent' ? 1 : undefined;

const getArithmeticOp = (operators: string) => {
  switch (operators) {
    case '==': {
      return 0;
    }
    case '>':
    case '&gt;': {
      return 1;
    }
    case '>=':
    case '&gt;=': {
      return 2;
    }
    case '<':
    case '&lt;': {
      return 3;
    }
    case '<=':
    case '&lt;=': {
      return 4;
    }
    case '%': {
      return 5;
    }
  }
};

const getParamsData = (params: Ast[], type: string) => {
  return params.map((item) => {
    const { operators, value, source } = item.attrsMap;
    const param = {
      arithmetic_op: getArithmeticOp(operators),
      flags: 0,
      source_id: dataItemStyle[source],
      value: Number(value),
      totalLength: PARAM_TOTAL_LENGTH
    };
    if (type === 'objEvent') {
      param.source_id = 0;
      param.value = DeviceEventMap[`${type}@${value}`];
    } else if (isNaN(param.value)) {
      param.flags = 1;
      param.value = dataItemStyle[value];
    }
    return param;
  });
};

const getCompareData = (compares: Ast[]) => {
  return compares.map((item) => {
    const { type, logic_op } = item.attrsMap;
    const params = getParamsData(item.children, type);
    const totalLength =
      COMPARE_PRE_LENGTH +
      params.reduce((prev, current) => prev + current.totalLength, 0);
    return {
      logical_op: getLogicalOp(logic_op as AnimationTrigger['logicalOperator']),
      type: getType(type),
      count: params.length,
      params,
      totalLength
    };
  });
};

/**
 * @description 统计当前条件下绑定的数据源，并全部存放在数组当中
 * @param compares Ast[] compare 树数组
 * @returns number[]
 */
const getDataSrcs = (compares: Ast[]) => {
  const srcs: number[] = [];
  // 如果存在事件的话，data_src 要默认最前面添加一个0
  let insetEventflag = false;
  compares.forEach((item) => {
    const { children, attrsMap } = item;
    if (attrsMap.type === 'objEvent') {
      insetEventflag = true;
      return;
    }
    if (attrsMap.type === 'data') {
      children.map((param) => {
        if (!param.attrsMap.source) return;
        const { source, value } = param.attrsMap;
        const data_src = dataItemStyle[source];
        srcs.push(data_src);
        const value_src = dataItemStyle[value];
        if (value_src) {
          srcs.push(value_src);
        }
      });
    }
  });
  if (insetEventflag) {
    srcs.unshift(0);
  }
  return Array.from(new Set(srcs));
};
/**
 * @description 计算当前条件下的数据源数量
 * @param compares compare 树数组
 * @returns number 数据源数量
 */
const getDataCount = (compares: Ast[]): number => {
  // 如果存在事件的话，需要将数据源的数量默认加一
  let defaultCount = 0;
  const sourceSet = new Set();
  compares.forEach((compare: Ast) => {
    const { type } = compare.attrsMap;
    if (type === 'objEvent') {
      defaultCount = 1;
      return;
    }
    compare.children.forEach((params: Ast) => {
      const { source, value } = params.attrsMap;
      sourceSet.add(source);
      if (isNaN(Number(value))) {
        sourceSet.add(value);
      }
    });
  });

  return Array.from(sourceSet).length + defaultCount;
};

export const getConditionData = (resource: Ast, curDataAddress: number) => {
  const { name, logic_op } = resource.attrsMap;
  const type = CONDITION;

  // 计算一下data_count
  const data_count = getDataCount(resource.children);
  const data_src = getDataSrcs(resource.children);
  const compares = getCompareData(resource.children);
  const totalLength =
    CONDITION_PRE_LENGTH + // 前置长度
    data_src.length * 2 + // data_src 长度
    compares.reduce((prev, current) => prev + current.totalLength, 0); // compare 长度

  const originData = {
    type,
    name,
    logical_op: getLogicalOp(logic_op as AnimationTrigger['logicalOperator']),
    data_count,
    compare_count: compares.length,
    data_src,
    compares,
    needDataSrc: data_src.length > 0,
    totalLength
  };
  // 记录
  const uid = genId(type);
  const originRecord = genRecord(
    type,
    name,
    uid,
    curDataAddress,
    originData.totalLength || 0
  );
  return { originData, originRecord };
};
